import { Clickable, Col, Row, Text } from 'components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMobileOS, openMobileAppLink } from 'core';
import { theme } from 'lib';

// Carried into the app so it knows the fan arrived via the P4P Day QR rather than an
// ordinary link. The native routers map this path to the campaign team's Team Channel.
//
// The team comes from the same env var the rest of the app uses for its default team, so this
// cannot name one team while the app's routers and the backend's auto-follow use another.
const P4P_DAY_TEAM = process.env.REACT_APP_DEFAULT_TEAM_USERNAME || 'fctokyo';
const APP_TARGET_PATH = `p4pday?source=p4pday&team=${P4P_DAY_TEAM}`;

// Japanese first (this is a Tokyo matchday), then English after a wider gap so the two
// blocks read as two languages rather than six unrelated lines.
const LANGUAGE_BLOCKS = [
  { lng: 'ja', marg: '24px 0 0' },
  { lng: 'en', marg: '32px 0 0' }
];

/**
 * P4P Day QR landing page.
 *
 * The printed QR (stadium pamphlets, booth signage, FC Tokyo's public announcement) points
 * here. On a phone this hands straight off to the native app, falling back to the right store
 * when it isn't installed; on desktop — where pre-event scanning from the website and social
 * media actually happens — it shows both store badges instead.
 *
 * Everything below renders immediately and without the API. 27,000 fans are expected to hit
 * this on stadium wifi, so the page must never depend on a round-trip to be useful, and must
 * still say something sensible if JS is slow. That is also why the copy lives in i18n rather
 * than being served by the backend.
 *
 * The supporting copy is printed in BOTH languages at once — Japanese block first, then the
 * English one — rather than in the device language. The QR is scanned by a mixed crowd in a
 * Tokyo stadium, and a fan whose phone is set to the other language still has to be able to
 * read why nothing opened. So these lines are pinned to 'ja'/'en' instead of using the
 * ambient language, and the heading stays English on every device per the typography spec.
 */
const P4PDay = () => {
  const { t } = useTranslation();
  const [os] = useState(() => getMobileOS());
  const isDesktop = os === 'other';

  useEffect(() => {
    if (isDesktop) return;
    // openMobileAppLink handles device detection, the app-scheme attempt, Safari's
    // "Open in app?" freeze, and the store fallback.
    openMobileAppLink(APP_TARGET_PATH);
  }, [isDesktop]);

  return (
    // The tint sits on the page column itself rather than an extra wrapper: this Col is a flex
    // child of the router layout, and wrapping it in a plain div drops the centring. minHt keeps
    // the colour edge-to-edge when the content is shorter than the screen.
    <Col centerAll pad="40px 24px" ht="100%" minHt="100vh" bg={theme.PAGE_BG_LIGHT_PURPLE}>
      <Col noFlex center wid="100%" style={{ maxWidth: '420px' }}>
        {/* Two rows, tight together: they read as one heading. */}
        <Text h1 center>
          {t('p4pDay.headerLine1', { lng: 'en' })}
        </Text>
        <Text h1 center>
          {t('p4pDay.headerLine2', { lng: 'en' })}
        </Text>

        {LANGUAGE_BLOCKS.map(({ lng, marg }) => (
          <Col key={lng} noFlex center wid="100%" marg={marg}>
            <Text b1 center>
              {isDesktop ? t('p4pDay.desktop', { lng }) : t('p4pDay.opening', { lng })}
            </Text>

            {!isDesktop && (
              <Text b2 center marg="8px 0 0">
                {t('p4pDay.notInstalled', { lng })}
              </Text>
            )}

            <Text b2 center marg="8px 0 0">
              {t('p4pDay.followPrompt', { lng })}
            </Text>
          </Col>
        ))}

        {/* Always rendered, on every device: the automatic hand-off can silently fail
            (in-app browsers, blocked schemes), and this is the fan's way out. */}
        <Row gap="12px" center wrapFlex marg="32px 0 0">
          <Clickable onClick={() => openMobileAppLink(false)}>
            <img src={require('assets/images/app-store.png')} alt="App Store" />
          </Clickable>

          <Clickable onClick={() => openMobileAppLink(true)}>
            <img src={require('assets/images/google-play.png')} alt="Google Play" />
          </Clickable>
        </Row>
      </Col>
    </Col>
  );
};

export default P4PDay;
